// src/App.jsx
import React, { useState, useEffect, useMemo, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import Dashboard from "./Dashboard";

// 👇 usa SOLO UNO dei due
import Login from "./components/Login"; // <--- usa questo
import Registration from "./features/onboarding/Registration";
import VerifyEmail from "./features/onboarding/VerifyEmail";
import Activation from "./features/onboarding/Activation";
// import Login from "./Login";      // <--- se il tuo file è qui, scommenta questo e commenta quello sopra

// Feature reali
import Abbatimenti from "./features/abbattimenti/Abbatimenti";
import FrighiPanel from "./features/frighi/FrighiPanel";
import RicevimentoMerci from "./features/ricevimento/RicevimentoMerci";
import Sanificazione from "./features/sanificazione/Sanificazione";
import RegistroSanificazione from "./features/sanificazione/RegistroSanificazione";
import ProcedureListPage from "./features/procedure/ProcedureListPage";
import ProcedureViewer from "./features/procedure/ProcedureViewer";


// Modulo etichette (pagina con tab Stampa + Storico)
import EtichettePage from "./features/etichette/EtichettePage";

import Admin from "./features/admin/Admin";
import RequireAdmin from "./features/admin/RequireAdmin";
import ProfiloAzienda from "./features/admin/ProfiloAzienda";

// Parla con Norma (NEW)
import ChatNorma from "./features/norma/ChatNorma";

// Autoconfigurazione
import SetupWizard from "./features/setup/SetupWizard";

import CryoManualEntry from "./features/frighi/CryoManualEntry";
import CryoQrPrint from "./features/frighi/CryoQrPrint";

// Auth helpers
import { checkSession } from "./api_auth";
import { setUnauthorizedHandler } from "./http";

import ChecklistIgienePersonale from "./features/igiene/ChecklistIgienePersonale";
import RegistroAbbattimenti from "./features/abbattimenti/RegistroAbbattimenti";
import ManualePage from "./features/manuale/ManualePage";
import DichiarazioneSelvaggina from "./features/ricevimento/DichiarazioneSelvaggina";
import AuditHaccpPage from "./features/AuditHaccp/AuditHaccpPage";
import Infestanti from "./features/infestanti/Infestanti";
import Manutenzioni from "./features/manutenzioni/Manutenzioni";
import RichiediCorso from "./features/corsi/RichiediCorso";
import ShopPage from "./features/shop/ShopPage";
import { canAccessShop } from "./features/shop/access";
import AllegatiGestionePage from "./features/allegati/AllegatiGestionePage";
import NonConformitaPage from "./features/nonconformita/NonConformitaPage";
import PanicModePage from "./features/panic/PanicModePage";
import InspectorAccessPage from "./features/panic/InspectorAccessPage";
import InspectorPortalPage from "./features/panic/InspectorPortalPage";
import InspectorSectionPrintPage from "./features/panic/InspectorSectionPrintPage";

import Termini from "./components/Termini";
import Privacy from "./components/Privacy";
import CookiePolicy from "./components/CookiePolicy";
import Security from "./components/Security";
import Footer from "./components/Footer";
import MobileBottomBar from "./components/MobileBottomBar";
import TrialPopupManager from "./components/TrialPopupModal";
import AppBannerManager from "./features/banners/AppBannerManager";
import InstallAppPopup from "./components/InstallAppPopup";
import Registri from "./features/registri/Registri";
import ArchivioDdt from "./components/ArchivioDdt";
import LorawanDevicesPage from "./features/admin/LorawanDevicesPage";
import MultiLocationDashboard from "./features/multilocation/MultiLocationDashboard";
import CambioOlio from "./features/friggitrice/CambioOlio";




/* ----------------------------------------
   Guard: login richiesto
---------------------------------------- */
function RequireAuth({ ready, autenticato, children }) {
  const location = useLocation();
  if (!ready) return <div style={{ padding: 16 }}>Verifica sessione…</div>;
  if (!autenticato) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

function SubscriptionGate({ ready, required, children }) {
  const location = useLocation();
  const publicPaths = ["/store", "/attiva", "/login", "/registrati", "/norma-pro-annuale", "/verifica-email", "/termini", "/privacy", "/cookie-policy", "/security"];
  if (!ready) return <div className="p-4">Verifica sessione…</div>;
  if (ready && required && !publicPaths.includes(location.pathname)) return <Navigate to="/attiva" replace />;
  return children;
}

/* ----------------------------------------
   Guard: autoconfigurazione
---------------------------------------- */
function RequireSetup({ ready, needSetup, children }) {
  if (!ready) return <div style={{ padding: 16 }}>Verifica configurazione…</div>;
  if (needSetup) return <Navigate to="/setup" replace />;
  return children;
}

/* ----------------------------------------
   SetupRoute
---------------------------------------- */
function RequirePerm({ codes, children }) {
  const [state, setState] = useState({ loading: true, allowed: false });
  const requiredCodes = useMemo(() => (Array.isArray(codes) ? codes : [codes]), [codes]);

  useEffect(() => {
    let alive = true;
    fetch("/api/me.php", { credentials: "include" })
      .then((res) => res.json().catch(() => null))
      .then((me) => {
        if (!alive) return;
        const perms = Array.isArray(me?.permessi) ? me.permessi : [];
        const allowed =
          me?.ruolo === "superadmin" ||
          perms.includes("ALL") ||
          requiredCodes.some((code) => perms.includes(code));
        setState({ loading: false, allowed });
      })
      .catch(() => {
        if (alive) setState({ loading: false, allowed: false });
      });
    return () => {
      alive = false;
    };
  }, [requiredCodes]);

  if (state.loading) return <div style={{ padding: 16 }}>Verifica permessi...</div>;
  if (!state.allowed) return <Navigate to="/" replace />;
  return children;
}

function RequireShop({ children }) {
  const [state, setState] = useState({ loading: true, allowed: false });

  useEffect(() => {
    let alive = true;
    fetch("/api/me.php", { credentials: "include" })
      .then((res) => res.json().catch(() => null))
      .then((me) => {
        if (!alive) return;
        setState({ loading: false, allowed: canAccessShop(me) });
      })
      .catch(() => {
        if (alive) setState({ loading: false, allowed: false });
      });
    return () => {
      alive = false;
    };
  }, []);

  if (state.loading) return <div style={{ padding: 16 }}>Verifica permessi...</div>;
  if (!state.allowed) return <Navigate to="/" replace />;
  return children;
}

function SetupRoute({ onDone }) {
  return (
    <SetupWizard
      onCompleted={() => {
        onDone?.();
        setTimeout(() => {
          window.location.href = "/";
        }, 200);
      }}
    />
  );
}

function InspectorOnlyGate({ ready, autenticato, inspectorOnly, children }) {
  const location = useLocation();

  if (!ready || !autenticato || !inspectorOnly) return children;

  const allowedPaths = [
    "/store",
    "/ispettore",
    "/ispettore-access",
    "/ispettore/stampa/",
    "/profilo-azienda",
    "/manuale",
    "/archivio-ddt",
    "/etichette",
    "/infestanti",
    "/manutenzioni",
    "/procedure",
    "/procedure/",
    "/registri",
    "/sanificazione/registro",
    "/non-conformita",
    "/privacy",
    "/termini",
    "/cookie-policy",
    "/security",
    "/multilocation",
  ];
  const pathAllowed = allowedPaths.some((path) =>
    path.endsWith("/") ? location.pathname.startsWith(path) : location.pathname === path
  );
  if (pathAllowed) {
    return children;
  }

  return <Navigate to="/ispettore" replace />;
}

export default function App() {
  const [autenticato, setAutenticato] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [inspectorOnly, setInspectorOnly] = useState(false);
  const [multiLocationManager, setMultiLocationManager] = useState(false);
  const [currentAziendaId, setCurrentAziendaId] = useState(null);

  const [needSetup, setNeedSetup] = useState(true);
  const [setupReady, setSetupReady] = useState(false);

  // interceptor 401
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSubscriptionRequired(false);
      setAutenticato(false);
      setInspectorOnly(false);
      setMultiLocationManager(false);
      setCurrentAziendaId(null);
      setNeedSetup(true);
      setSetupReady(true);
    });
  }, []);

  // bootstrap sessione
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const info = await checkSession();
        if (!alive) return;
        setAutenticato(!!info.authenticated);
        setSubscriptionRequired(!!info.requires_subscription);
        setNeedSetup(!!info.requires_setup);
        setInspectorOnly(!!info.inspector_only);
        setMultiLocationManager(!!info.is_multilocation_manager);
        setCurrentAziendaId(info.azienda_id ?? null);
      } catch {
        if (!alive) return;
        setAutenticato(false);
        setInspectorOnly(false);
        setMultiLocationManager(false);
        setCurrentAziendaId(null);
        setNeedSetup(true);
      } finally {
        if (alive) {
          setAuthReady(true);
          setSetupReady(true);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function doLogout() {
    setSubscriptionRequired(false);
    try {
      await fetch("/api/logout.php", { method: "POST", credentials: "include" });
    } catch { }

    // ✅ reset stato frontend (QUESTO è il punto chiave)
    setAutenticato(false);
    setInspectorOnly(false);
    setMultiLocationManager(false);
    setCurrentAziendaId(null);
    setNeedSetup(true);
    setSetupReady(true);
    setAuthReady(true);
  }

  // quando diventi autenticato, rilegge lo stato setup
  useEffect(() => {
    let alive = true;
    if (!autenticato) return;
    (async () => {
      try {
        setSetupReady(false);
        const info = await checkSession();
        if (!alive) return;
        setSubscriptionRequired(!!info.requires_subscription);
        setNeedSetup(!!info.requires_setup);
        setInspectorOnly(!!info.inspector_only);
        setMultiLocationManager(!!info.is_multilocation_manager);
        setCurrentAziendaId(info.azienda_id ?? null);
      } catch {
        if (!alive) return;
        setNeedSetup(true);
        setInspectorOnly(false);
        setMultiLocationManager(false);
        setCurrentAziendaId(null);
      } finally {
        if (alive) setSetupReady(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [autenticato]);

  return (
    <Router>
      <div className="min-h-screen flex flex-col">
        <AppBannerManager
          enabled={authReady && autenticato && !subscriptionRequired && setupReady && !inspectorOnly && !!currentAziendaId && !needSetup}
          companyId={currentAziendaId}
        />
        <InstallAppPopup
          enabled={authReady && autenticato && !subscriptionRequired && !inspectorOnly && !!currentAziendaId && !needSetup}
        />
        <TrialPopupManager
          enabled={authReady && autenticato && !subscriptionRequired && !inspectorOnly && !!currentAziendaId && !needSetup}
        />
        <div className="flex-1">
          <Suspense fallback={<div style={{ padding: 16 }}>Caricamento…</div>}>
            <SubscriptionGate ready={authReady} required={subscriptionRequired}>
            <InspectorOnlyGate ready={authReady} autenticato={autenticato} inspectorOnly={inspectorOnly}>
            <Routes>
              <Route path="/store" element={<ShopPage publicStore />} />
              <Route path="/registrati" element={<Registration key="standard" />} />
              <Route path="/norma-pro-annuale" element={<Registration key="annual" offer="annual" />} />
              <Route path="/verifica-email" element={<VerifyEmail />} />
              <Route path="/attiva" element={<RequireAuth ready={authReady} autenticato={autenticato}><Activation /></RequireAuth>} />
              <Route path="/ispettore-access" element={<InspectorAccessPage />} />
              <Route
                path="/ispettore"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <InspectorPortalPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/ispettore/stampa/:sectionId"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <InspectorSectionPrintPage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />
              {/* LOGIN - una sola rotta */}
              <Route
                path="/login"
                element={
                  autenticato ? (
                    <Navigate to={subscriptionRequired ? "/attiva" : inspectorOnly ? "/ispettore" : multiLocationManager && !currentAziendaId ? "/multilocation" : needSetup ? "/setup" : "/"} replace />
                  ) : (
                    <Login
                      onLogin={async (loginData) => {
                        try {
                          const info = await checkSession();
                          const nextNeedSetup = !!info.requires_setup;
                          const nextInspectorOnly = !!info.inspector_only;
                          setNeedSetup(nextNeedSetup);
                          setInspectorOnly(nextInspectorOnly);
                          setMultiLocationManager(!!info.is_multilocation_manager);
                          setCurrentAziendaId(info.azienda_id ?? null);
                          setAutenticato(true);
                          setSubscriptionRequired(!!info.requires_subscription);
                          window.location.replace(info.requires_subscription ? "/attiva" : nextInspectorOnly
                            ? "/ispettore"
                            : info.is_multilocation_manager && !info.azienda_id
                              ? "/multilocation"
                            : nextNeedSetup
                              ? "/setup"
                              : (loginData?.redirect_to || "/"));
                        } catch {
                          setAutenticato(true);
                          setNeedSetup(true);
                          setInspectorOnly(false);
                          setMultiLocationManager(false);
                          setCurrentAziendaId(null);
                          window.location.replace(loginData?.redirect_to || "/");
                        }
                      }}
                    />
                  )
                }
              />

              {/* SETUP */}
              <Route
                path="/setup"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    {setupReady && !needSetup ? (
                      <Navigate to="/" replace />
                    ) : (
                      <SetupRoute onDone={() => setNeedSetup(false)} />
                    )}
                  </RequireAuth>
                }
              />

              {/* SETUP EDIT (riapre sempre configuratore) */}
              <Route
                path="/setup/edit"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    {/* non usare RequireSetup: deve essere accessibile anche se setup è completato */}
                    <SetupWizard
                      mode="edit"
                      onCompleted={() => {
                        // in edit non è necessario, ma non fa male
                        setNeedSetup(false);
                      }}
                    />
                  </RequireAuth>
                }
              />

              {/* DASHBOARD */}
              <Route
                path="/"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    {inspectorOnly ? (
                      <Navigate to="/ispettore" replace />
                    ) : multiLocationManager && !currentAziendaId ? (
                      <Navigate to="/multilocation" replace />
                    ) : (
                      <RequireSetup ready={setupReady} needSetup={needSetup}>
                        <Dashboard onLogout={doLogout} />
                      </RequireSetup>
                    )}
                  </RequireAuth>
                }
              />

              <Route
                path="/multilocation"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <MultiLocationDashboard />
                  </RequireAuth>
                }
              />

              {/* NORMA */}
              <Route
                path="/norma"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RequirePerm codes="NORMA_AI">
                        <ChatNorma />
                      </RequirePerm>
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* PANIC MODE */}
              <Route
                path="/panic"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RequirePerm codes={["PANIC_REPORT", "PANIC_MODE"]}>
                        <PanicModePage />
                      </RequirePerm>
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* FRIGHI */}
              <Route
                path="/profilo-azienda"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <ProfiloAzienda me={null} canEdit={false} />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              <Route
                path="/frighi"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <FrighiPanel />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* RICEVIMENTO MERCI */}
              <Route
                path="/ricevimento"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RicevimentoMerci />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* ALLEGATI DI GESTIONE */}
              <Route
                path="/allegati-gestione"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <AllegatiGestionePage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />


              {/* PROCEDURE - LISTA */}
              <Route
                path="/procedure"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <ProcedureListPage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* PROCEDURE - VIEWER */}
              <Route
                path="/procedure/:slug"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <ProcedureViewer />
                    </RequireSetup>
                  </RequireAuth>
                }
              />



              {/* DICHIARAZIONE SELVAGGINA */}
              <Route
                path="/selvaggina/:ricevimentoId"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <DichiarazioneSelvaggina />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* Richiedi corso */}
              <Route
                path="/richiedi-corso"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RequireAdmin><RichiediCorso /></RequireAdmin>
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* Shop */}
              <Route
                path="/shop"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RequireShop>
                        <ShopPage onLogout={doLogout} />
                      </RequireShop>
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* ETICHETTE (Stampa + Storico) */}
              <Route
                path="/etichette"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <EtichettePage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* ABBATTIMENTI */}
              <Route
                path="/abbattimenti"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <Abbatimenti />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              <Route
                path="/abbattimenti/registro"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RegistroAbbattimenti />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* MANUTENZIONI */}
              <Route
                path="/manutenzioni"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <Manutenzioni />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* NON CONFORMITÀ (Allegato 4) */}
              <Route
                path="/non-conformita"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <NonConformitaPage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* SANIFICAZIONE */}
              <Route
                path="/sanificazione"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <Sanificazione />
                    </RequireSetup>
                  </RequireAuth>
                }
              />
              <Route
                path="/sanificazione/registro"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RegistroSanificazione />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* MANUALE HACCP */}
              <Route
                path="/manuale"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <ManualePage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* AUDIT HACCP (Allegato 14) */}
              <Route
                path="/audit-haccp"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <AuditHaccpPage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* INFESTANTI (Monitoraggio interno) */}
              <Route
                path="/infestanti"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <Infestanti />
                    </RequireSetup>
                  </RequireAuth>
                }
              />


              {/* IGIENE PERSONALE */}
              <Route
                path="/igiene-personale"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <ChecklistIgienePersonale reparto="Cucina" />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* VERIFICA OLII FRITTURA */}
              <Route
                path="/verifica-olii-frittura"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RequirePerm codes="OLII_FRITTURA">
                        <CambioOlio onLogout={doLogout} />
                      </RequirePerm>
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              <Route
                path="/frighi/manual/:id"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <CryoManualEntry />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              <Route
                path="/frighi/qr/:id"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <CryoQrPrint />
                    </RequireSetup>
                  </RequireAuth>
                }
              />


              {/* ADMIN → va messo PRIMA del catch-all */}
              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <Admin />
                  </RequireAdmin>
                }
              />

              <Route path="/registri" element={<Registri />} />

              <Route path="/archivio-ddt" element={<ArchivioDdt />} />

              <Route
                path="/admin/lorawan-devices"
                element={
                  <RequireAdmin>
                    <LorawanDevicesPage />
                  </RequireAdmin>
                }
              />

              {/* INFO LEGALI */}
              <Route path="/termini" element={<Termini />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/security" element={<Security />} />

              {/* CATCH-ALL */}
              <Route
                path="*"
                element={
                  <Navigate
                    to={autenticato ? (inspectorOnly ? "/ispettore" : multiLocationManager && !currentAziendaId ? "/multilocation" : needSetup ? "/setup" : "/") : "/login"}
                    replace
                  />
                }
              />
            </Routes>
            </InspectorOnlyGate>
            </SubscriptionGate>
          </Suspense>
        </div>

        {authReady && autenticato && !subscriptionRequired && setupReady && !needSetup && !inspectorOnly && !!currentAziendaId ? (
          <MobileBottomBar />
        ) : null}

        <Footer />
      </div>
    </Router>
  );
}
